/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import { useCookies } from 'react-cookie';
import { useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, useState, Fragment, useRef } from 'react';

import { ArrowLeftOutlined, FovDivider, FovLoader, FovOption, FovSelect, H4, HorizontalSpacer, SearchInputField, VerticalSpacer } from 'components';
import { PlusOutlined } from 'components/icons';
import { clearOrgModelsAdmin, getOrganizationsAdmin, getOrgModelsAdmin } from 'store/actions/adminActions/organizations';

import { AddOrganizationForm } from './AddOrganizationForm';
import { MemberContainer, AddOrganizationButton, TitleContainer, TabContainer, Tab } from './styles';
import { listColumns } from './ListColumns';
import { HeaderContainer, IconButton, SortContainer } from 'pages/ArchVisionAdminDashboard/styles';
import { Settings } from './Settings';
import { OrgModels } from './OrgModels';

const AdminOrganizations = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);
    const searchFieldRef = useRef('');

    
    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;
    
    const selectedTheme = userDataStore.userPreferences.theme;
    const organizations = useMemo(() => adminDataStore.organizations, [ adminDataStore.organizations ]);
    const isLoading = useMemo(() => adminDataStore.isOrgLoading, [ adminDataStore.isOrgLoading ]);
    const pagination = useMemo(() => adminDataStore.organizationsPagination, [ adminDataStore.organizationsPagination ]);
    const { page, pageSize, totalCount } = pagination;
    
    const [ showAddOrganizationForm, setShowAddOrganizationForm ] = useState(false);
    const [ selectedOrg, setSelectedOrg ] = useState(null);
    const [ selectedTab, setSelectedTab ] = useState('settings');
    const [ searchParams, setSearchParams ] = useSearchParams();


    useEffect(() => {
        if (!organizations || organizations.length === 0) {
            dispatch(getOrganizationsAdmin(token));
        }
    }, []);

    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;
        searchParams.set('page', page);
        setSearchParams(searchParams);
        dispatch(getOrganizationsAdmin(token));
    };

    const handleBlur = () => {
        const value = searchFieldRef.current.value;

        if (value === '') {
            searchParams.delete('search');
            searchParams.set('page', 1);
            setSearchParams(searchParams);
            dispatch(getOrganizationsAdmin(token));
            return;
        }

        searchParams.set('search', value);
        searchParams.set('page', 1);
        setSearchParams(searchParams);
        dispatch(getOrganizationsAdmin(token));
    };

    const sortOptions = [
        { key: 'NAME_ASC', text: 'Name (A-Z)', orderKey: 'order_by' },
        { key: 'NAME_DESC', text: 'Name (Z-A)', orderKey: 'order_by' },
        { key: 'OCCUPIED_SEATS_ASC', text: 'Occupied Seats ↑', orderKey: 'order_by' },
        { key: 'OCCUPIED_SEATS_DESC', text: 'Occupied Seats ↓', orderKey: 'order_by' },
    ];

    const buildSortOptions = () => sortOptions.map(option => (
        <FovOption key={option.key} value={option.key}>
            {option.text}
        </FovOption>
    ));

    const selectedSortOption = sortOptions.find(opt => {
        return searchParams.get('order_by') === opt.key;
    });

    const tabIsSelected = name => selectedTab === name;

    return (
        <FovLoader
            text='Retrieving organizations data'
            resource={!isLoading}
        >
            {organizations?.length === 0 && <div>No organizations.</div>}
            {organizations?.length > 0 && !selectedOrg &&
                <Fragment>
                    <HeaderContainer>
                        <SearchInputField
                            width={400}
                            disabled={false}
                            ref={searchFieldRef}
                            defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                            onChange={e => searchFieldRef.current.value = e.target.value}
                            onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                            onBlur={handleBlur}
                        />
                        <SortContainer>
                            <FovSelect
                                value={selectedSortOption?.key || ''}
                                placeholder={'Sort by'}
                                onChange={(_e, newVal) => {
                                    const sortOption = sortOptions.find(opt => opt.key === newVal);
                                    searchParams.delete('order_by');
                                    searchParams.set(sortOption.orderKey, sortOption.key);
                                    searchParams.set('page', 1);
                                    setSearchParams(searchParams);
                                    dispatch(getOrganizationsAdmin(token));
                                }}
                            >
                                {buildSortOptions()}
                            </FovSelect>
                        </SortContainer>
                        <HorizontalSpacer size={8} />
                        <AddOrganizationButton
                            $selectedTheme={selectedTheme}
                            onClick={() => {
                                setShowAddOrganizationForm(true);
                            }}
                        >
                            <>Add Organization</>
                            <HorizontalSpacer size={6} />
                            <PlusOutlined width={20} />
                        </AddOrganizationButton>
                    </HeaderContainer>
                    <MemberContainer
                        dataSource={organizations}
                        $selectedTheme={selectedTheme}
                        columns={listColumns(theme, selectedTheme, setSelectedOrg)}
                        rowKey={'row-' + Math.random()}
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        isPagination={true}
                        selectedKey={'id'}
                        onPageChange={handlePageChange}
                    />
                    <AddOrganizationForm
                        isVisible={showAddOrganizationForm}
                        onClose={() => setShowAddOrganizationForm(false)}
                    />
                </Fragment>
            }
            {selectedOrg &&
                <Fragment>
                    <TitleContainer>
                        <H4>{selectedOrg.name}</H4>
                        <IconButton
                            theme={theme}
                            $selectedTheme={selectedTheme}
                            backgroundColor={'onPrimary'}
                            hoverColor={theme.colors[selectedTheme].accentHover}
                            onClick={() => {
                                setSelectedOrg(null);
                                searchParams.delete('organizationId');
                                searchParams.delete('page');
                                setSearchParams(searchParams);
                                setSelectedTab('settings');
                                dispatch(clearOrgModelsAdmin());
                            }}
                            icon={
                                <ArrowLeftOutlined
                                    theme={theme}
                                    color={theme.colors[selectedTheme].primary}
                                />
                            }
                        />
                    </TitleContainer>
                    <TabContainer>
                        <Tab
                        theme={theme}
                        $selectedTheme={selectedTheme}
                        onClick={() => {
                            setSelectedTab('settings');
                        }}
                        selected={tabIsSelected('settings')}
                    >
                        Settings
                    </Tab>
                    <Tab
                        theme={theme}
                        $selectedTheme={selectedTheme}
                        onClick={() => {
                            setSelectedTab('models');
                            searchParams.set('organizationId', selectedOrg.id);
                            setSearchParams(searchParams);
                            dispatch(getOrgModelsAdmin(token, selectedOrg.id));
                        }}
                        selected={tabIsSelected('models')}
                    >
                        Models
                    </Tab>
                    </TabContainer>
                    <FovDivider />
                    <VerticalSpacer size={16}/>
                    {selectedTab === 'settings' && <Settings organization={selectedOrg}/>}
                    {selectedTab === 'models' && <OrgModels organization={selectedOrg}/>}
                </Fragment>
            }
        </FovLoader>
    );
});


export default AdminOrganizations;